import { EventBus } from '@/shared/lib/event-bus';

const MINIMAL_DROPDOWN_LIST_HEIGHT = 220;

export default {
  props: {
    options: {
      type: Array,
      default: () => []
    },
    valueKey: {
      type: String,
      default: 'value'
    },
    labelKey: {
      type: String,
      default: 'label'
    },
    dropdownPosition: {
      type: String,
      default: 'auto',
      validator: (value) => ['auto', 'top', 'bottom'].includes(value)
    },
    hideDropdownWhenEmpty: {
      type: Boolean,
      default: false
    },
    emptyText: {
      type: String,
      default: 'No options available'
    },
    filterMode: {
      type: String,
      default: 'local',
      validator: (value) => ['local', 'remote'].includes(value),
    },
  },

  data() {
    return {
      isDropdownOpen: false,
      focusedIndex: -1,
      dropdownTop: 0,
      dropdownDirection: 'bottom',
      searchQuery: '',
    };
  },

  mounted() {
    document.addEventListener('click', this.handleDropdownClickOutside);
    EventBus.$on('select:close', this.closeDropdown);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleDropdownClickOutside);
    EventBus.$off('select:close', this.closeDropdown);
  },

  computed: {
    filteredOptions() {
      if (this.filterMode === 'remote') return this.options;

      if (this.searchQuery) {
        const selectedLabel = this.value
          ? this.getOptionLabel(
            this.options.find(opt => this.getOptionValue(opt) === this.getOptionValue(this.value))
          )
          : null;

        if (this.searchQuery === selectedLabel) return this.options;

        const query = this.searchQuery.toLowerCase();
        return this.options.filter(option =>
          this.getOptionLabel(option).toLowerCase().includes(query)
        );
      }

      return this.options;
    },

    shouldShowDropdown() {
      return this.isDropdownOpen &&
        !(this.hideDropdownWhenEmpty && !this.searchQuery && this.filteredOptions.length === 0);
    },
  },

  methods: {
    getOptionValue(option) {
      return option && typeof option === 'object' ? option[this.valueKey] : option;
    },

    getOptionLabel(option) {
      if (!option) return '';
      return typeof option === 'object' ? option[this.labelKey] : option;
    },

    isSelected(option) {
      if (!this.value) return false;
      return this.getOptionValue(option) === this.getOptionValue(this.value);
    },

    computeDropdownDirection() {
      if (this.dropdownPosition !== 'auto') {
        this.dropdownDirection = this.dropdownPosition;
        return;
      }

      const wrapper = this.$el;
      if (!wrapper) {
        this.dropdownDirection = 'bottom';
        return;
      }

      const wrapperRect = wrapper.getBoundingClientRect();
      const spaceBelow = window.innerHeight - wrapperRect.bottom;
      const spaceAbove = wrapperRect.top;

      this.dropdownDirection =
        spaceBelow >= MINIMAL_DROPDOWN_LIST_HEIGHT || spaceBelow >= spaceAbove
          ? 'bottom'
          : 'top';
    },

    openDropdown() {
      EventBus.$emit('select:close');

      this.$nextTick(() => {
        this.computeDropdownDirection();
        this.isDropdownOpen = true;
        this.focusedIndex = this.filteredOptions.findIndex(opt => this.isSelected(opt));

        this.$nextTick(() => {
          this.setDropdownPosition();
          this.scrollToSelected();
        });
      });
    },

    closeDropdown() {
      this.isDropdownOpen = false;
      this.focusedIndex = -1;
      this.onDropdownClose();
    },

    onDropdownClose() {
      this.searchQuery = '';
    },

    toggleDropdown() {
      this.isDropdownOpen ? this.closeDropdown() : this.openDropdown();
    },

    selectOption(option) {
      const selected = this.options.find(opt => this.getOptionValue(opt) === option);
      this.$emit('beforeChange', option, this.value);
      this.$emit('input', option);
      this.$emit('change', option);
      this.$emit('select', selected);
      this.closeDropdown();
    },

    navigateOptions(direction) {
      if (!this.isDropdownOpen) {
        this.openDropdown();
        return;
      }

      const len = this.filteredOptions.length;
      const newIndex = this.focusedIndex + direction;

      if (newIndex >= 0 && newIndex < len) {
        this.focusedIndex = newIndex;
      }

      this.$nextTick(() => this.scrollToSelected());
    },

    selectFocusedOption() {
      if (this.focusedIndex >= 0 && this.focusedIndex < this.filteredOptions.length) {
        this.selectOption(this.getOptionValue(this.filteredOptions[this.focusedIndex]));
      }
    },

    scrollToSelected() {
      const selectedOption = this.$el.querySelector('.dropdown__option.is-selected');
      const dropdown = this.$el.querySelector('.dropdown__list');

      if (selectedOption && dropdown) {
        const selectedTop = selectedOption.offsetTop;
        const selectedHeight = selectedOption.offsetHeight;
        const dropdownHeight = dropdown.clientHeight;
        dropdown.scrollTop = selectedTop - dropdownHeight / 2 + selectedHeight / 2;
      }
    },

    setDropdownPosition() {
      const wrapper = this.$el;
      if (!wrapper) return;

      const wrapperRect = wrapper.getBoundingClientRect();

      if (this.dropdownDirection === 'bottom') {
        this.dropdownTop = wrapperRect.height + 1;
        return;
      }

      const list = wrapper.querySelector('.dropdown__list');
      this.dropdownTop = list ? -list.offsetHeight - 5 : wrapperRect.height + 1;
    },

    handleDropdownClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.closeDropdown();
      }
    },

    handleDropdownKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.navigateOptions(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.navigateOptions(-1);
          break;
        case 'Enter':
          event.preventDefault();
          this.selectFocusedOption();
          break;
        case 'Escape':
          this.closeDropdown();
          break;
      }
    },
  },
};
